import { useId, useState } from "react"
import { DownOutlined, InboxOutlined } from "@ant-design/icons"
import { Button, Flex, Input, Tooltip } from "antd"
import { useShallow } from "zustand/react/shallow"
import { Alert, AnalyticsAction, ScriptCreationIcon, Text } from "~/components"
import { useHotVideoStore } from "~/store/hotVideo"
import { useExpandedSectionScroll } from "../useExpandedSectionScroll"
import styles from "./index.css"

export const ProductInfo = () => {
  const [expanded, setExpanded] = useState(false)
  const sectionRef = useExpandedSectionScroll(expanded)
  const id = useId()
  const {
    productInfo,
    isAnalyzingProduct,
    productInfoError,
    setProductInfo,
    analyzeProductImages,
    videoImageUrls,
    resourcesPending,
  } = useHotVideoStore(
    useShallow((state) => ({
      productInfo: state.productInfo,
      isAnalyzingProduct: state.isAnalyzingProduct,
      productInfoError: state.productInfoError,
      setProductInfo: state.setProductInfo,
      analyzeProductImages: state.analyzeProductImages,
      videoImageUrls: state.videoImageUrls,
      resourcesPending: state.resourcesPending,
    })),
  )
  const disabledReason = resourcesPending
    ? "素材正在上传或处理中，请稍候"
    : !videoImageUrls.trim()
      ? "请先上传商品素材"
      : ""

  return (
    <Flex
      ref={sectionRef}
      className={styles.section}
      data-product-collapsed={!expanded || undefined}
      vertical
      gap={12}
    >
      <Flex
        align="center"
        justify="space-between"
      >
        <button
          className={styles.sectionToggle}
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((value) => !value)}
        >
          <Flex
            component="span"
            align="center"
            gap={8}
          >
            <InboxOutlined className={styles.sectionIcon} />
            <span>商品信息</span>
            <DownOutlined
              className={styles.sectionArrow}
              rotate={expanded ? 180 : 0}
            />
          </Flex>
        </button>
        <Tooltip title={disabledReason}>
          <AnalyticsAction
            action="点击AI帮写"
            prefix="爆款复刻"
          >
            <Button
              className={styles.writeButton}
              size="small"
              icon={<ScriptCreationIcon />}
              loading={isAnalyzingProduct}
              disabled={Boolean(disabledReason)}
              onClick={() => {
                setExpanded(true)
                void analyzeProductImages()
              }}
            >
              <Text
                size="s"
                color="inherit"
              >
                AI帮写
              </Text>
            </Button>
          </AnalyticsAction>
        </Tooltip>
      </Flex>
      <div
        id={id}
        hidden={!expanded}
      >
        <Flex
          vertical
          gap={16}
        >
          <fieldset
            className={styles.productFields}
            disabled={isAnalyzingProduct}
          >
            <Flex
              vertical
              gap={12}
            >
              <Flex
                vertical
                gap={4}
              >
                <label
                  className={styles.fieldLabel}
                  htmlFor={`${id}-name`}
                >
                  <Text
                    size="s"
                    type="secondary"
                  >
                    商品名称
                  </Text>
                </label>
                <Input
                  id={`${id}-name`}
                  className={styles.productInput}
                  placeholder="例如：防滑地垫"
                  value={productInfo.name}
                  onChange={(event) =>
                    setProductInfo({ name: event.target.value })
                  }
                />
              </Flex>
              <Flex
                vertical
                gap={4}
              >
                <label
                  className={styles.fieldLabel}
                  htmlFor={`${id}-category`}
                >
                  <Text
                    size="s"
                    type="secondary"
                  >
                    商品品类
                  </Text>
                </label>
                <Input
                  id={`${id}-category`}
                  className={styles.productInput}
                  placeholder="例如：家居用品"
                  value={productInfo.category}
                  onChange={(event) =>
                    setProductInfo({ category: event.target.value })
                  }
                />
              </Flex>
              <Flex
                vertical
                gap={4}
              >
                <label
                  className={styles.fieldLabel}
                  htmlFor={`${id}-audience`}
                >
                  <Text
                    size="s"
                    type="secondary"
                  >
                    目标人群
                  </Text>
                </label>
                <Input
                  id={`${id}-audience`}
                  className={styles.productInput}
                  placeholder="例如：租房党、宝妈"
                  value={productInfo.targetUsers}
                  onChange={(event) =>
                    setProductInfo({ targetUsers: event.target.value })
                  }
                />
              </Flex>
              <Flex
                vertical
                gap={4}
              >
                <label
                  className={styles.fieldLabel}
                  htmlFor={`${id}-selling`}
                >
                  <Text
                    size="s"
                    type="secondary"
                  >
                    核心卖点
                  </Text>
                </label>
                <Input.TextArea
                  id={`${id}-selling`}
                  className={styles.productInput}
                  placeholder={"每行一个，例如：\n吸水\n防滑\n颜值高"}
                  autoSize={{ minRows: 4 }}
                  value={productInfo.sellingPoints}
                  onChange={(event) =>
                    setProductInfo({ sellingPoints: event.target.value })
                  }
                />
              </Flex>
            </Flex>
          </fieldset>
          {productInfoError ? (
            <Alert
              level="strong"
              title="商品信息生成失败"
              description={productInfoError}
            />
          ) : null}
        </Flex>
      </div>
    </Flex>
  )
}
